import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import TimelineText from '@/v2/components/atoms/TimelineText';
import TimeController from '@/v2/components/controllers/TimeController';
import Text from '@/v2/components/atoms/Text';
import Header from '@/v2/components/application/Header';
import Circle from '@/v2/components/atoms/Circle';
import Phone from '@/v2/components/atoms/Icon/Phone';
import SteeringWheel from '@/v2/components/atoms/Icon/SteeringWheel';
import SmartHome from '@/v2/components/atoms/Icon/SmartHome';
import Disclaimer from '@/v2/components/layouts/Disclaimer';
import { saveTimeout, clearTimeouts } from '@/v2/components/utils/Timeout';
import { ConnectivityContext } from '@/v2/state/providers';
import { StateContext } from '@/v2/state/store';
import Fade from '@/v2/components/atoms/Fade';

import './Overview.scss';

const propTypes = {
    type: PropTypes.string,
};
const defaultProps = {
    type: null,
};

const Overview = ({ isActive, data, onFinished, onReady, type, secondaryType }) => {
    const [activeType, setActiveType] = useState(type);
    const [isReady, setReady] = useState(false);
    const [isEnabled, setEnabled] = useState(false);
    const [isFinishing, setFinishing] = useState(false);
    const [isAnimatingOut, setAnimatingOut] = useState(false);
    const [{ disclaimer }] = useContext(ConnectivityContext);
    const store = useContext(StateContext);
    const { state, dispatch } = store;

    let timeline = { ...data.text.timeline };
    const time = useRef(TimelineText.Time());

    const ready = () => {
        if (isReady) return;

        onReady();
        setReady(true);
    };

    const activate = () => {
        setEnabled(true);
        const timeController = new TimeController(time.current.Set);
        saveTimeout(
            timeController.AddTimeoutFromTimeline(timeline, () => {
                if (!type && secondaryType) {
                    setTimeout(() => {
                        setActiveType(secondaryType);
                        setTimeout(() => {
                            setFinishing(true);
                        }, 5000);
                    }, 1000);
                } else {
                    setAnimatingOut(true);
                    setTimeout(() => {
                        setFinishing(true);
                    }, 1000);
                }
            })
        );
    };

    useEffect(() => () => clearTimeouts(), []);

    useEffect(() => {
        activate();
        if (isActive) {
            setFinishing(false);
        }
        ready();
    }, [isActive]);

    useEffect(() => {
        if (activeType) {
            const typeData = data.icons.filter((item) => item.type === activeType)[0];
            timeline = {};
            timeline[typeData.duration] = typeData.label.bottomText;
        }
        if (activeType === secondaryType) {
            activate();
        }
    }, [activeType]);

    useEffect(() => {
        if (isActive && isReady && !state.lightBgEnabled) {
            dispatch({ type: 'SET_BACKGROUND_LIGHT' });
        }
    });

    const getAnimationClass = (icon) => {
        if (!activeType || (activeType === 'incar' && icon === 'incar' && !isAnimatingOut)) {
            return 'animate-in';
        }

        if ((activeType === 'app' && icon !== activeType) || (activeType === 'incar' && icon === 'incar' && isAnimatingOut)) {
            return 'animate-out';
        }

        return null;
    };

    const getVisibilityClass = (icon) => {
        if (activeType && icon !== activeType) {
            return getAnimationClass(icon) ? getAnimationClass(icon) : 'hidden';
        }
        return ['visible', getAnimationClass(icon)].join(' ');
    };

    const getMenuItemName = (icon) => data.icons.find((item) => item.type === icon);

    const disclaimerContent = isActive && disclaimer ? <Disclaimer data={disclaimer} dark /> : '';

    return (
        <div
            className={`Overview ${activeType && activeType.length > 0 ? `Overview--${activeType}` : ''}`}
            style={{ background: `url(${data.image}) center`, backgroundSize: 'cover' }}
        >
            <Header show={isActive} animate={!type} hideLogo dark />
            <div className="Overview__inner">
                {isActive && (
                    <>
                        <div className="Overview__menu">
                            <img src={`${window.HELIOS.publicPath}images/overview-circle.svg`} alt="overview" className="Overview__menu-outline" />
                            <div>
                                {[
                                    { type: 'app', icon: <Phone /> },
                                    { type: 'incar', icon: <SteeringWheel /> },
                                    { type: 'home', icon: <SmartHome /> },
                                ].map((item) => (
                                    <div key={item.type} className={['Overview__menu-item', getVisibilityClass(item.type)].join(' ')}>
                                        <Text className={['Overview__menu-item-title', getAnimationClass(item.type)].join(' ')} component="h5">
                                            {getMenuItemName(item.type).label.title}
                                        </Text>
                                        <Circle>{item.icon}</Circle>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
            {isActive && <TimelineText key={activeType} data={timeline} color="black" time={time.current} />}
            {disclaimerContent}
            <Fade
                cover
                show={(!isEnabled || isFinishing) && Boolean(activeType)}
                onShowEnd={isFinishing ? onFinished : () => {}}
                // Only in-car overview fades to dark when leaving
                light={activeType !== 'incar' || (activeType === 'incar' && !isFinishing)}
                dark={activeType === 'incar' && isFinishing}
            />
        </div>
    );
};

Overview.propTypes = propTypes;
Overview.defaultProps = defaultProps;

export default Overview;
